import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Search, Download, RefreshCw, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SFGInventoryReport = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ totalGroups: 0, totalBundles: 0, totalPieces: 0, uniqueBins: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    bin_code: '',
    design_name: '',
    barcode: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // 2026-05-16 — Backfill blank design_name on SFG storage rows from
  // the latest Goods Transfer FG Assignment. Two-step: dry-run shows
  // what would change, then user confirms to apply.
  const [isBackfilling, setIsBackfilling] = useState(false);
  const handleBackfillDesignNames = async () => {
    setIsBackfilling(true);
    try {
      const preview = await apiClient.post(
        '/sections/dispatch/backfill-sfg-design-name',
        { dry_run: true, limit: 5000 },
      );
      const d = preview.data || {};
      const toUpdate = d.unique_bundles_to_update ?? d.unique_bundles_updated ?? 0;
      const stillBlank = d.still_blank_count ?? 0;
      if (toUpdate === 0) {
        toast.info(
          stillBlank > 0
            ? `No matching FG assignments found for ${stillBlank} blank bundle(s). Make sure FG Assignment is saved on Goods Transfer.`
            : 'No SFG rows with blank design_name found.',
          { duration: 8000 },
        );
        return;
      }
      const proceed = window.confirm(
        `${toUpdate} bundle(s) will be backfilled with design names from the latest FG Assignment.\n\n` +
        (stillBlank > 0 ? `${stillBlank} bundle(s) have no matching assignment yet and will be skipped.\n\n` : '') +
        'Apply the backfill now?',
      );
      if (!proceed) {
        toast.info('Backfill cancelled — nothing changed.');
        return;
      }
      const apply = await apiClient.post(
        '/sections/dispatch/backfill-sfg-design-name',
        { dry_run: false, limit: 5000 },
      );
      const a = apply.data || {};
      toast.success(
        `Backfilled ${a.unique_bundles_updated || 0} bundle(s): ${a.sfg_rows_updated || 0} SFG row(s), ${a.dispatch_rows_mirrored || 0} dispatch row(s).`,
        { duration: 10000 },
      );
      // Refresh the inventory list to show the new design names.
      await fetchInventory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Backfill failed';
      toast.error(`Backfill failed: ${msg}`, { duration: 10000 });
    } finally {
      setIsBackfilling(false);
    }
  };

  // 2026-05-26 — Backfill missing usn_count / usn_codes on STORED SFG rows.
  // User reported a bundle showing `Total Pieces: 0` here while the
  // Put-Away Entries report showed `Qty: 4` (FE falls back to
  // `case_pack_size`). Root cause: GT put-away inserted the SFG row
  // before `dispatch_received_bundle_usns` carried USN data, so the row
  // landed with `usn_count = 0` / empty `usn_codes`. This button is
  // idempotent — only touches rows where USN identity is still missing.
  const [isBackfillingPieces, setIsBackfillingPieces] = useState(false);
  const handleBackfillBlankPieces = async () => {
    setIsBackfillingPieces(true);
    try {
      const preview = await apiClient.post(
        '/wms/sfg/inventory/backfill-blank-pieces',
        { dry_run: true, limit: 5000 },
      );
      const d = preview.data || {};
      const toUpdate = d.rows_to_update ?? 0;
      const found = d.rows_found ?? 0;
      const skipped = d.rows_skipped ?? 0;
      if (toUpdate === 0) {
        toast.info(
          found === 0
            ? 'No STORED rows with missing piece counts found. Nothing to backfill.'
            : `${found} STORED row(s) have missing piece counts but none can be recovered from dispatch / GT / inhouse sources.`,
          { duration: 9000 },
        );
        return;
      }
      const sampleLines = (d.sample_updates || []).slice(0, 5).map(
        (s) => `  • ${s.bundle_code} (${s.bin_location || '—'}): 0 → ${s.new_usn_count} pcs [${s.source}]`
      ).join('\n');
      const proceed = window.confirm(
        `${toUpdate} bundle(s) will have their piece counts restored from dispatch_received_bundle_usns / goods_transfer_bundles / inhouse_bundle_receipts.\n\n` +
        (sampleLines ? `Sample:\n${sampleLines}\n\n` : '') +
        (skipped > 0 ? `${skipped} bundle(s) have no recoverable USN data and will be skipped.\n\n` : '') +
        'Apply the backfill now?',
      );
      if (!proceed) {
        toast.info('Backfill cancelled — nothing changed.');
        return;
      }
      const apply = await apiClient.post(
        '/wms/sfg/inventory/backfill-blank-pieces',
        { dry_run: false, limit: 5000 },
      );
      const a = apply.data || {};
      toast.success(
        `Backfilled ${a.rows_updated || 0} bundle(s) with piece counts. ` +
        (a.rows_skipped > 0 ? `${a.rows_skipped} unrecoverable were skipped.` : ''),
        { duration: 10000 },
      );
      await fetchInventory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Backfill failed';
      toast.error(`Backfill failed: ${msg}`, { duration: 10000 });
    } finally {
      setIsBackfillingPieces(false);
    }
  };

  const fetchInventory = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.bin_code) params.append('bin_code', filters.bin_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.barcode) params.append('barcode', filters.barcode);
      params.append('limit', '50000');
      
      const response = await apiClient.get(`/wms/sfg/inventory-report?${params.toString()}`);
      
      if (response.data.success) {
        setInventory(response.data.inventory || []);
        setStats({
          totalGroups: response.data.total_groups || 0,
          totalBundles: response.data.total_bundles || 0,
          totalPieces: response.data.total_pieces || 0,
          uniqueBins: response.data.unique_bins || 0
        });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory report');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleSearch = () => {
    fetchInventory();
  };

  const handleClearFilters = () => {
    setFilters({ bin_code: '', design_name: '', barcode: '' });
  };

  const handleExportCSV = () => {
    if (inventory.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Location Name', 'Bin Status', 'Barcode', 'Design Name', 'Size', 'Shade', 'Bundles', 'Total Pieces', 'Bundle Numbers'];
    const csvContent = [
      headers.join(','),
      ...inventory.map(item => [
        item.location_name,
        item.bin_locked ? `"Locked${(item.bin_lock_block_put_away ? ' Put-away' : '') + (item.bin_lock_block_picking ? ' Picking' : '')}"` : 'Open',
        item.barcode,
        `"${item.design_name}"`,
        item.size,
        item.shade,
        item.bundle_count,
        item.total_pieces,
        `"${(item.bundle_codes || []).join(', ')}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sfg_inventory_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // ==========================================================================
  // USN-wise chunked CSV export (up to 100,000 USNs per file) — respects the
  // page's active filters, covers STORED + ALLOCATED + RESERVED statuses.
  // ==========================================================================
  const [isExportingUSN, setIsExportingUSN] = useState(false);

  const buildUSNExportParams = () => {
    const p = new URLSearchParams();
    if (filters.bin_code) p.append('bin_code', filters.bin_code);
    if (filters.design_name) p.append('design_name', filters.design_name);
    if (filters.barcode) p.append('barcode', filters.barcode);
    return p;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const handleExportUSNsCSV = async () => {
    if (isExportingUSN) return;
    const PART_SIZE = 100000;
    setIsExportingUSN(true);
    try {
      // STEP 1 — ask backend for total USN count so we know how many parts to request
      const countParams = buildUSNExportParams();
      countParams.append('part_size', String(PART_SIZE));
      const countRes = await apiClient.get(`/wms/sfg/export-usn-csv/count?${countParams.toString()}`);
      const totalUsns = countRes.data?.total_usns || 0;
      const totalParts = countRes.data?.total_parts || 0;

      if (totalUsns === 0) {
        toast.error('No USNs match the current filters');
        return;
      }

      toast.info(
        totalParts === 1
          ? `Exporting ${totalUsns.toLocaleString()} USN(s) in 1 file…`
          : `Exporting ${totalUsns.toLocaleString()} USN(s) across ${totalParts} file(s)…`
      );

      const dateStr = new Date().toISOString().split('T')[0];

      // STEP 2 — sequentially download each part
      for (let part = 1; part <= totalParts; part += 1) {
        const params = buildUSNExportParams();
        params.append('part', String(part));
        params.append('part_size', String(PART_SIZE));

        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/wms/sfg/export-usn-csv?${params.toString()}`, {
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const filename = `sfg_inventory_usns_${dateStr}_part${part}-of-${totalParts}.csv`;
        downloadBlob(blob, filename);

        // Small delay so the browser can sequence the save dialogs
        if (part < totalParts) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 350));
        }
      }

      toast.success(
        totalParts === 1
          ? `Exported ${totalUsns.toLocaleString()} USN(s)`
          : `Exported ${totalUsns.toLocaleString()} USN(s) across ${totalParts} files`
      );
    } catch (error) {
      console.error('USN export failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to export USN CSV');
    } finally {
      setIsExportingUSN(false);
    }
  };

  const handleExportOrphanedCSV = async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/export-orphaned-bundles-csv', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `orphaned_bundles_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('Orphaned bundles CSV downloaded');
    } catch (error) {
      toast.error('Failed to export orphaned bundles');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/sfg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">SFG Inventory Report</h1>
                <p className="text-sm text-[#E8DCC8]">View bundles stored in bins</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportOrphanedCSV}
              className="bg-red-500/20 border-red-400/30 text-red-200 hover:bg-red-500/30"
              data-testid="export-orphaned-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Orphaned Bundles
            </Button>
            <Button
              variant="outline"
              onClick={handleExportUSNsCSV}
              disabled={isExportingUSN}
              className="bg-amber-500/20 border-amber-400/30 text-amber-100 hover:bg-amber-500/30 disabled:opacity-60"
              data-testid="export-usn-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {isExportingUSN ? 'Exporting…' : 'USN CSV'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Item Groups</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalGroups.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Bundles</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalBundles.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pieces</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalPieces.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Bins Used</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.uniqueBins.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackfillDesignNames}
                  disabled={isBackfilling}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="backfill-sfg-design-btn"
                  title="Back-propagate FG Assignment design names onto SFG rows that show blank design name."
                >
                  {isBackfilling ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Backfill Blank Designs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackfillBlankPieces}
                  disabled={isBackfillingPieces}
                  className="border-rose-300 text-rose-800 hover:bg-rose-50"
                  data-testid="backfill-sfg-pieces-btn"
                  title="Restore missing piece counts on STORED bundles by reading USN data from dispatch / goods-transfer / inhouse sources. Use when SFG Inventory shows 0 pieces but the bundle is physically present."
                >
                  {isBackfillingPieces ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Backfill Blank Pieces
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Bin Code</label>
                  <Input
                    placeholder="Filter by bin..."
                    value={filters.bin_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bin_code: e.target.value }))}
                    data-testid="filter-bin"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Design Name</label>
                  <Input
                    placeholder="Filter by design..."
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">SKU / Bundle Code</label>
                  <Input
                    placeholder="Filter by barcode..."
                    value={filters.barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, barcode: e.target.value }))}
                    data-testid="filter-barcode"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    className="w-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Inventory Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Location Name</TableHead>
                    <TableHead className="font-semibold">Bin Status</TableHead>
                    <TableHead className="font-semibold">SKU Code</TableHead>
                    <TableHead className="font-semibold">Design Name</TableHead>
                    <TableHead className="font-semibold">Size</TableHead>
                    <TableHead className="font-semibold">Shade</TableHead>
                    <TableHead className="font-semibold text-right">Bundles</TableHead>
                    <TableHead className="font-semibold text-right">Total Pieces</TableHead>
                    <TableHead className="font-semibold">Bundle Numbers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : inventory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No inventory found. Try adjusting your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    inventory.map((item, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm">{item.location_name}</TableCell>
                        <TableCell data-testid={`sfg-bin-status-${index}`}>
                          {item.bin_locked ? (
                            <Badge className="bg-red-100 text-red-800 border border-red-200">
                              Locked{(() => {
                                const b = [];
                                if (item.bin_lock_block_put_away) b.push('Put-away');
                                if (item.bin_lock_block_picking) b.push('Picking');
                                return b.length ? ` · ${b.join(' + ')}` : '';
                              })()}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 border border-green-200">Open</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                        <TableCell>{item.design_name}</TableCell>
                        <TableCell>{item.size || '-'}</TableCell>
                        <TableCell>{item.shade || '-'}</TableCell>
                        <TableCell className="text-right font-semibold">{item.bundle_count}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">{item.total_pieces}</TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-xs">
                          {(item.bundle_codes || []).slice(0, 3).join(', ')}
                          {(item.bundle_codes || []).length > 3 && (
                            <span className="text-blue-600 ml-1">+{item.bundle_codes.length - 3} more</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {inventory.length > 0 && (
              <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {inventory.length} item groups
                </span>
                <Badge variant="outline" className="text-lg px-4 py-1">
                  Total Pieces: {stats.totalPieces.toLocaleString()}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SFGInventoryReport;
